/**
 * Contractor Fetch API
 *
 * API functions for fetching contractors
 */

import { buildApiUrl, handleApiResponse } from '@/src/shared/api';
import { MOCK_CONTRACTORS } from '../model/mock';
import { mapContractorsToCardProps } from '../lib/mappers';
import type { ContractorCardProps } from '../ui/ContractorCard';
import type { ContractorApiResponse } from '../lib/mappers';

export interface FetchContractorsParams {
  categoryIds: string[];
  latitude?: number;
  longitude?: number;
  radius?: number;
  minRate?: number;
  maxRate?: number;
  minRating?: number;
  useMock?: boolean;
}

/**
 * Fetch contractors by category IDs
 */
export async function fetchContractorsByCategories(
  params: FetchContractorsParams,
): Promise<ContractorCardProps[]> {
  const { categoryIds, latitude, longitude, radius, minRate, maxRate, minRating, useMock = false } = params;

  if (categoryIds.length === 0) {
    return [];
  }

  // Use mock data for development
  if (useMock) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(MOCK_CONTRACTORS);
      }, 800);
    });
  }

  // Real API call - using /contractors/public/search endpoint
  const queryParams = new URLSearchParams();

  // Add category IDs
  categoryIds.forEach(id => queryParams.append('categoryIds', id));

  // Add optional geolocation parameters
  if (latitude !== undefined) {
    queryParams.append('latitude', latitude.toString());
  }
  if (longitude !== undefined) {
    queryParams.append('longitude', longitude.toString());
  }
  if (radius !== undefined) {
    queryParams.append('radius', radius.toString());
  }

  // Add filter parameters
  if (minRate !== undefined) {
    queryParams.append('minRate', minRate.toString());
  }
  if (maxRate !== undefined) {
    queryParams.append('maxRate', maxRate.toString());
  }
  if (minRating !== undefined) {
    queryParams.append('minRating', minRating.toString());
  }

  // Set limit
  queryParams.append('limit', '50');

  const url = buildApiUrl(`/contractors/public/search?${queryParams.toString()}`);

  const response = await fetch(url);
  const data = await handleApiResponse<ContractorApiResponse[]>(response);

  return mapContractorsToCardProps(data || []);
}
